import { Matrix4, Quaternion, Vector3 } from 'three';
import { watchImmediate } from '@vueuse/core';
import type { StopHandle } from '@/util/stopHandle';
import { vector3 } from '@/domain/geometry/vector3';
import type { RenderingState } from '@/lib/threeScene/renderingState';
import { orthoWorldPerScreenPx } from '@/lib/threeScene/cameras/orthoUnits';
import { isLandmarkMarkerNode } from '@/views/3d-planner/hipSpine/scene/landmarkSceneSync';
import type { AnnotationGroup, HipSpinePostureViewState } from '@/views/3d-planner/hipSpine/types';

/**
 * On-screen radius a marker holds at every zoom level. 7 px is what the old
 * world-unit radius already drew at fit zoom: a radius of `0.008 * imageHeight`
 * over a world-per-pixel of `imageHeight / viewportHeightPx` cancels to
 * `0.008 * viewportHeightPx`.
 */
export const LANDMARK_MARKER_SCREEN_RADIUS_PX = 7;

/** A marker transform is a translation and a uniform scale — never a rotation. */
const NO_ROTATION = new Quaternion();

/** Null until the viewport is measured; markers keep their build-time radius. */
export function landmarkMarkerWorldRadius(rendering: RenderingState): number | null {
    const viewport = rendering.viewportSize;
    if (!viewport) return null;
    const worldPerPx = orthoWorldPerScreenPx({
        frustumHeight: rendering.camera.height,
        canvasHeightPx: viewport.height,
        zoom: rendering.camera.zoom,
    });
    return LANDMARK_MARKER_SCREEN_RADIUS_PX * worldPerPx;
}

/** Replaces the scale, keeping the position. */
function setUniformScale(transform: Matrix4, scale: number): void {
    const position = new Vector3().setFromMatrixPosition(transform);
    transform.compose(position, NO_ROTATION, vector3(scale, scale, scale));
}

/**
 * Markers share one unit-radius sphere, so their radius is entirely the node
 * transform's scale. Deliberately skips the femur-head circle (anatomical: a
 * real radius, so it scales with the image) and the labels (placed by overlap
 * resolution in image pixels).
 */
export function applyLandmarkMarkerRadius(groups: AnnotationGroup[], worldRadius: number): void {
    for (const group of groups) {
        for (const child of group.children) {
            if (isLandmarkMarkerNode(child)) setUniformScale(child.transform, worldRadius);
        }
    }
}

/**
 * Holds the markers at a constant on-screen size as the camera zooms, and
 * across a geometry rebuild.
 *
 * A reactive projection rather than a per-render-pass write, so the node
 * transform stays the single source of the marker's size and raycast
 * hit-testing picks exactly what is drawn. The loupe's world-per-pixel is
 * always the main camera's over LOUPE_MAGNIFICATION, so one radius renders
 * there at a fixed multiple — still constant, still zoom-independent.
 */
export function updateLandmarkMarkerScale(
    rendering: RenderingState,
    viewState: HipSpinePostureViewState,
): StopHandle {
    return watchImmediate(
        () =>
            [
                rendering.camera.zoom,
                rendering.camera.height,
                rendering.viewportSize,
                viewState.annotationGroups,
            ] as const,
        ([, , , groups]) => {
            const worldRadius = landmarkMarkerWorldRadius(rendering);
            if (worldRadius === null) return;
            applyLandmarkMarkerRadius(groups, worldRadius);
        },
        // The scene re-registers groups synchronously on reassignment, so a later
        // flush would publish the build-time radius to the renderer and to picking.
        { flush: 'sync' },
    );
}
